/**
 * Web Speech API Voice Synthesizer with automatic Chrome resume,
 * word boundaries, character-wise male/female/robot voice selection, and Web Audio fallback.
 */
import { EventEmitter } from '../core/EventEmitter';
import { LipSync, VisemeType } from '../characters/LipSync';
import { AudioManager } from './AudioManager';

export interface SpeechRequest {
  text: string;
  gender?: 'male' | 'female' | 'robot';
  voiceName?: string;
  rate?: number;
  pitch?: number;
  lang?: string;
  language?: string;
  onWord?: (word: string, charIndex: number) => void;
  onViseme?: (viseme: VisemeType) => void;
  onEnd?: () => void;
}

export class VoicePlayer extends EventEmitter {
  private synth: SpeechSynthesis | null = null;
  private isSpeaking: boolean = false;
  private currentUtterance: SpeechSynthesisUtterance | null = null;
  private audioManager: AudioManager = new AudioManager();
  private voices: SpeechSynthesisVoice[] = [];
  private speechTimer: any = null;
  private defaultLanguage: string = 'mr-IN';

  constructor() {
    super();
    if (typeof window !== 'undefined' && 'speechSynthesis' in window) {
      this.synth = window.speechSynthesis;
      this.loadVoices();
      if (this.synth.onvoiceschanged !== undefined) {
        this.synth.onvoiceschanged = () => this.loadVoices();
      }
    }
  }

  public setDefaultLanguage(lang?: string): void {
    if (lang) {
      const bcp = VoicePlayer.mapLanguageToBcp47(lang);
      if (bcp) this.defaultLanguage = bcp;
    }
  }

  public getDefaultLanguage(): string {
    return this.defaultLanguage;
  }

  public getIsSpeaking(): boolean {
    if (typeof window !== 'undefined' && window.speechSynthesis) {
      return this.isSpeaking || window.speechSynthesis.speaking;
    }
    return this.isSpeaking;
  }

  /**
   * Estimates realistic spoken duration in seconds for a text string,
   * accounting for language cadence (Indic vs English), word syllables,
   * punctuation pauses (commas, periods), and natural start/end margins.
   */
  public static estimateSpokenDuration(text: string, lang?: string): number {
    const raw = (text || '').trim();
    if (!raw) return 4.0;

    const words = raw.split(/\s+/).filter(Boolean);
    const wordCount = words.length;
    if (wordCount === 0) return 4.0;

    // Detect language or script
    const bcp = VoicePlayer.mapLanguageToBcp47(lang) || VoicePlayer.detectScriptLanguage(raw);
    const isIndic = bcp && (bcp.startsWith('mr') || bcp.startsWith('hi') || bcp.startsWith('gu') || bcp.startsWith('bn') || bcp.startsWith('ta') || bcp.startsWith('te') || bcp.startsWith('kn') || bcp.startsWith('ml') || bcp.startsWith('pa') || bcp.startsWith('or') || bcp.startsWith('sa'));

    // Speaking rate: Indic languages average ~1.5 to 1.7 words/sec; English averages ~2.0 words/sec
    const wordsPerSec = isIndic ? 1.6 : 2.0;
    const baseSpeechSec = wordCount / wordsPerSec;

    // Punctuation pauses (commas, semicolons: ~0.35s; periods, question/exclamation: ~0.6s)
    const shortPauses = (raw.match(/[,;]/g) || []).length * 0.35;
    const longPauses = (raw.match(/[.!?]/g) || []).length * 0.6;
    const totalPauses = Math.min(6.0, shortPauses + longPauses);

    // Padding for speech start, sentence breath, and scene outro hold
    const padding = 2.5;

    return Math.max(4.0, Math.ceil(baseSpeechSec + totalPauses + padding));
  }

  public static mapLanguageToBcp47(langInput?: string): string | null {
    if (!langInput) return null;
    const clean = langInput.trim().toLowerCase();

    if (clean.includes('-')) return clean;

    const map: Record<string, string> = {
      'marathi': 'mr-IN',
      'मराठी': 'mr-IN',
      'mr': 'mr-IN',
      'hindi': 'hi-IN',
      'हिंदी': 'hi-IN',
      'hi': 'hi-IN',
      'gujarati': 'gu-IN',
      'ગુજરાતી': 'gu-IN',
      'gu': 'gu-IN',
      'bengali': 'bn-IN',
      'বাংলা': 'bn-IN',
      'bn': 'bn-IN',
      'tamil': 'ta-IN',
      'தமிழ்': 'ta-IN',
      'ta': 'ta-IN',
      'telugu': 'te-IN',
      'తెలుగు': 'te-IN',
      'te': 'te-IN',
      'kannada': 'kn-IN',
      'कन्नड': 'kn-IN',
      'ಕನ್ನಡ': 'kn-IN',
      'kn': 'kn-IN',
      'malayalam': 'ml-IN',
      'മലയാളം': 'ml-IN',
      'ml': 'ml-IN',
      'punjabi': 'pa-IN',
      'पंजाबी': 'pa-IN',
      'ਪੰਜਾਬੀ': 'pa-IN',
      'pa': 'pa-IN',
      'assamese': 'as-IN',
      'অসমীয়া': 'as-IN',
      'as': 'as-IN',
      'odia': 'or-IN',
      'ওড়িয়া': 'or-IN',
      'or': 'or-IN',
      'sanskrit': 'sa-IN',
      'संस्कृतम्': 'sa-IN',
      'sa': 'sa-IN',
      'maithili': 'mai-IN',
      'मैथिली': 'mai-IN',
      'english': 'en-US',
      'en': 'en-US',
      'spanish': 'es-ES',
      'es': 'es-ES',
      'french': 'fr-FR',
      'fr': 'fr-FR',
      'german': 'de-DE',
      'de': 'de-DE'
    };

    return map[clean] || (clean.length === 2 ? `${clean}-IN` : clean);
  }

  public static detectScriptLanguage(text: string): string | null {
    if (!text) return null;

    // Devanagari script range: \u0900-\u097F
    if (/[\u0900-\u097F]/.test(text)) {
      // Check Marathi specific characters: ळ, ॲ, ऑ or common Marathi words
      if (/[ळॲऑ]/.test(text) || /\b(आहे|आहेत|आणि|नाही|काय|कसे|मराठी|या|त्या|सर्व|कोण|माझे)\b/i.test(text)) {
        return 'mr-IN';
      }
      return 'hi-IN';
    }
    if (/[\u0980-\u09FF]/.test(text)) return 'bn-IN';
    if (/[\u0A00-\u0A7F]/.test(text)) return 'pa-IN';
    if (/[\u0A80-\u0AFF]/.test(text)) return 'gu-IN';
    if (/[\u0B00-\u0B7F]/.test(text)) return 'or-IN';
    if (/[\u0B80-\u0BFF]/.test(text)) return 'ta-IN';
    if (/[\u0C00-\u0C7F]/.test(text)) return 'te-IN';
    if (/[\u0C80-\u0CFF]/.test(text)) return 'kn-IN';
    if (/[\u0D00-\u0D7F]/.test(text)) return 'ml-IN';

    return null;
  }

  public loadVoices(): void {
    if (this.synth) {
      this.voices = this.synth.getVoices();
    }
  }

  public getAvailableVoices(): SpeechSynthesisVoice[] {
    if (this.voices.length === 0) {
      this.loadVoices();
    }
    return this.voices;
  }

  public selectVoice(
    gender: 'male' | 'female' | 'robot' = 'male',
    voiceName?: string,
    lang?: string,
    textSample?: string
  ): { voice: SpeechSynthesisVoice | null; defaultPitch: number; defaultRate: number; resolvedLang: string } {
    const all = this.getAvailableVoices();
    let defaultPitch = 1.0;
    let defaultRate = 1.0;

    if (gender === 'female') {
      defaultPitch = 1.2;
      defaultRate = 1.05;
    } else if (gender === 'male') {
      defaultPitch = 0.9;
      defaultRate = 1.0;
    } else if (gender === 'robot') {
      defaultPitch = 0.55;
      defaultRate = 0.95;
    }

    const resolvedLang = VoicePlayer.mapLanguageToBcp47(lang) || (textSample ? VoicePlayer.detectScriptLanguage(textSample) : null) || this.defaultLanguage || 'mr-IN';

    if (voiceName) {
      const exact = all.find(v => v.name.toLowerCase().includes(voiceName.toLowerCase()));
      if (exact) return { voice: exact, defaultPitch, defaultRate, resolvedLang: exact.lang || resolvedLang };
    }

    if (all.length === 0) {
      return { voice: null, defaultPitch, defaultRate, resolvedLang };
    }

    const langPrefix = resolvedLang.split('-')[0].toLowerCase(); // e.g. "mr", "hi", "en", "gu"

    // 1. Direct language code match (e.g. mr-IN, mr, hi-IN)
    let langVoices = all.filter(v => v.lang.toLowerCase().startsWith(langPrefix));

    // 2. Devanagari / Indian Voice Fallback for Marathi / Hindi
    if (langVoices.length === 0 && (langPrefix === 'mr' || langPrefix === 'hi')) {
      // Look for Hindi Devanagari voices (hi-IN) or Indian English voices (en-IN) which can speak Devanagari/Indian names
      langVoices = all.filter(v => v.lang.toLowerCase().startsWith('hi') || v.lang.toLowerCase().startsWith('en-in') || /marathi|hindi|kalpana|hemant|rishi|मराठी|हिंदी/i.test(v.name));
    }

    if (langVoices.length > 0) {
      // Prioritize Google TTS voices for maximum naturalness
      const googleVoice = langVoices.find(v => v.name.toLowerCase().includes('google'));
      if (googleVoice) {
        return { voice: googleVoice, defaultPitch, defaultRate, resolvedLang: googleVoice.lang };
      }

      if (gender === 'female') {
        const match = langVoices.find(v => /female|woman|girl|zira|samantha|kalpana|shruti/i.test(v.name));
        if (match) return { voice: match, defaultPitch, defaultRate, resolvedLang: match.lang };
      } else if (gender === 'male') {
        const match = langVoices.find(v => /male|man|boy|david|rishi|hemant/i.test(v.name));
        if (match) return { voice: match, defaultPitch, defaultRate, resolvedLang: match.lang };
      }
      return { voice: langVoices[0], defaultPitch, defaultRate, resolvedLang: langVoices[0].lang };
    }

    // 3. For Marathi / Hindi / Non-English: NEVER force an English voice object onto non-English script!
    // Returning voice: null allows browser native Web Speech engine to synthesize utt.lang = 'mr-IN' natively!
    if (langPrefix !== 'en') {
      return { voice: null, defaultPitch, defaultRate, resolvedLang };
    }

    // 4. English Fallback
    if (gender === 'female') {
      const femaleMatches = ['zira', 'samantha', 'victoria', 'karen', 'fiona', 'female', 'google us english'];
      const match = all.find(v => femaleMatches.some(m => v.name.toLowerCase().includes(m)));
      if (match) return { voice: match, defaultPitch, defaultRate, resolvedLang: match.lang };
    } else if (gender === 'male') {
      const maleMatches = ['david', 'george', 'mark', 'daniel', 'oliver', 'rishi', 'male', 'google uk english male'];
      const match = all.find(v => maleMatches.some(m => v.name.toLowerCase().includes(m)));
      if (match) return { voice: match, defaultPitch, defaultRate, resolvedLang: match.lang };
    }

    const fallback = all.find(v => v.lang.toLowerCase().startsWith('en')) || all[0] || null;
    return { voice: fallback, defaultPitch, defaultRate, resolvedLang: fallback?.lang || resolvedLang };
  }

  public speak(req: SpeechRequest): void {
    this.stop();

    if (typeof window !== 'undefined' && (window as any).__asae_recording_active__) {
      console.log('[VoicePlayer] TTS playback suppressed while recording microphone voiceover');
      if (req.onEnd) req.onEnd();
      return;
    }

    // Export mode: use backend TTS via AudioContext (capturable by MediaRecorder)
    // VideoExporter sets window.__asae_export_audio_ctx__ = { ctx, masterGain } before recording.
    const exportCtx = typeof window !== 'undefined'
      ? (window as any).__asae_export_audio_ctx__ as { ctx: AudioContext; masterGain: GainNode } | undefined
      : undefined;
    if (exportCtx?.ctx && req.text) {
      void this.speakExportMode(req, exportCtx.ctx, exportCtx.masterGain);
      return;
    }

    if (!this.synth || !req.text) {
      this.fallbackWordCadence(req);
      return;
    }

    try {
      if (this.synth.paused) {
        this.synth.resume();
      }
      this.synth.cancel();

      const selected = this.selectVoice(req.gender || 'male', req.voiceName, req.lang || req.language, req.text);

      const utt = new SpeechSynthesisUtterance(req.text);
      utt.rate = req.rate !== undefined ? req.rate : selected.defaultRate;
      utt.pitch = req.pitch !== undefined ? req.pitch : selected.defaultPitch;
      utt.lang = selected.resolvedLang || req.lang || req.language || 'en-US';

      if (selected.voice) {
        utt.voice = selected.voice;
      }

      let hadWordBoundary = false;

      utt.onboundary = (e: SpeechSynthesisEvent) => {
        if (e.name === 'word') {
          hadWordBoundary = true;
          const word = req.text.slice(e.charIndex).split(/\s+/)[0];
          req.onWord?.(word, e.charIndex);
          this.emit('word', { word, charIndex: e.charIndex });

          if (word.length > 0) {
            const viseme = LipSync.charToViseme(word[0]);
            req.onViseme?.(viseme);
            this.emit('viseme', viseme);
          }
        }
      };

      utt.onstart = () => {
        this.isSpeaking = true;
        this.emit('start', req.text);
      };

      utt.onend = () => {
        this.isSpeaking = false;
        this.currentUtterance = null;
        req.onEnd?.();
        this.emit('end');
      };

      utt.onerror = (e) => {
        console.warn('SpeechSynthesis error or speech blocked, triggering fallback cadence:', e);
        this.isSpeaking = false;
        this.currentUtterance = null;
        this.fallbackWordCadence(req);
      };

      this.isSpeaking = true;
      this.currentUtterance = utt;
      this.synth.speak(utt);

      // Chrome speech synthesis safety resume
      if (this.synth.paused) {
        this.synth.resume();
      }

      // Safety fallback if boundaries don't fire
      setTimeout(() => {
        if (this.isSpeaking && !hadWordBoundary) {
          this.fallbackWordCadence(req, false);
        }
      }, 300);

    } catch (err) {
      console.warn('Speech Synthesis exception:', err);
      this.fallbackWordCadence(req);
    }
  }

  /**
   * Export mode: fetches TTS audio from backend /api/voice/tts and plays via AudioContext.
   * AudioContext output IS captured by MediaStreamDestination added to MediaRecorder.
   * Fires fallbackWordCadence for lip-sync events (approximate timing).
   */
  private async speakExportMode(
    req: SpeechRequest,
    audioCtx: AudioContext,
    masterGain: GainNode,
  ): Promise<void> {
    const selected = this.selectVoice(req.gender || 'male', req.voiceName, req.lang || req.language, req.text);
    const lang   = selected.resolvedLang || req.lang || req.language || 'en-US';
    const gender = req.gender || 'male';

    this.isSpeaking = true;
    this.emit('start', req.text);

    // Fire word boundary events for lip-sync (approximate cadence)
    this.fallbackWordCadence(req, false);

    try {
      const url = `/api/voice/tts?text=${encodeURIComponent(req.text)}&lang=${encodeURIComponent(lang)}&gender=${encodeURIComponent(gender)}`;
      console.log('[VoicePlayer][Export] Fetching TTS from backend:', url.substring(0, 80));

      const response = await fetch(url);
      if (!response.ok) throw new Error(`Backend TTS HTTP ${response.status}`);

      const arrayBuffer = await response.arrayBuffer();
      if (arrayBuffer.byteLength === 0) throw new Error('Backend TTS returned empty audio');

      const audioBuffer = await audioCtx.decodeAudioData(arrayBuffer);
      console.log('[VoicePlayer][Export] Decoded audio duration:', audioBuffer.duration.toFixed(1), 's');

      // Stop fallback cadence timer (we now have real audio timing)
      if (this.speechTimer) { clearTimeout(this.speechTimer); this.speechTimer = null; }

      const source = audioCtx.createBufferSource();
      source.buffer = audioBuffer;
      source.connect(masterGain); // -> masterGain -> destination (speakers) + captureNode

      source.onended = () => {
        this.isSpeaking = false;
        this.currentUtterance = null;
        req.onEnd?.();
        this.emit('end');
        console.log('[VoicePlayer][Export] TTS playback ended.');
      };

      if (audioCtx.state === 'suspended') await audioCtx.resume();
      source.start(0);

    } catch (err: any) {
      console.error('[VoicePlayer][Export] Backend TTS failed, using fallback cadence:', err?.message || err);
      // On error: complete the fallback cadence and signal end
      this.isSpeaking = false;
      req.onEnd?.();
      this.emit('end');
    }
  }

  private fallbackWordCadence(req: SpeechRequest, invokeEnd = true): void {
    const words = req.text.split(/\s+/);
    if (words.length === 0) {
      if (invokeEnd) req.onEnd?.();
      return;
    }

    const intervalMs = Math.max(180, Math.min(380, 240 / (req.rate || 1.0)));
    let index = 0;

    const tickCadence = () => {
      if (index < words.length) {
        const word = words[index];
        req.onWord?.(word, index);
        this.emit('word', { word, charIndex: index });

        if (word.length > 0) {
          const viseme = LipSync.charToViseme(word[0]);
          req.onViseme?.(viseme);
          this.emit('viseme', viseme);
        }

        index++;
        this.speechTimer = setTimeout(tickCadence, intervalMs);
      } else {
        this.isSpeaking = false;
        if (invokeEnd) {
          req.onEnd?.();
          this.emit('end');
        }
      }
    };

    tickCadence();
  }

  public stop(): void {
    if (this.speechTimer) {
      clearTimeout(this.speechTimer);
      this.speechTimer = null;
    }
    if (this.synth) {
      try {
        this.synth.cancel();
      } catch {}
    }
    this.isSpeaking = false;
    this.currentUtterance = null;
  }
}
